import React, { useMemo, useState } from 'react'
import { Table, Switch, Input, Tag, Tooltip, message, Button, Space } from 'antd'
import { InfoCircleTwoTone } from '@ant-design/icons'
import { sortBy } from 'lodash'

import { isRepoActionable } from '../../api/repositories'

const HEALTHY_STATUSES = ['open', 'unarchived']

export default function RepositoriesList({ repositories, onToggle, onBulkToggle }) {
  const [search, setSearch] = useState('')
  const [pendingIds, setPendingIds] = useState({})

  const filtered = useMemo(() => {
    const sorted = sortBy(repositories || [], (repo) => repo.name?.toLowerCase())
    if (!search) return sorted
    const needle = search.toLowerCase()
    return sorted.filter(
      (repo) =>
        repo.name?.toLowerCase().includes(needle) ||
        repo.fullName?.toLowerCase().includes(needle),
    )
  }, [repositories, search])

  const handleToggle = async (repo, value) => {
    setPendingIds((prev) => ({ ...prev, [repo.id]: true }))
    try {
      await onToggle(repo, value)
      message.success(
        value
          ? `Axolo will now react to events in ${repo.name}.`
          : `Axolo will stop reacting to events in ${repo.name}.`,
      )
    } catch (error) {
      console.log('Error toggling repository openingPR', error)
      message.error("Couldn't update repository, try again or contact support.")
    } finally {
      setPendingIds((prev) => {
        const next = { ...prev }
        delete next[repo.id]
        return next
      })
    }
  }

  const columns = [
    {
      title: 'Repository',
      dataIndex: 'name',
      key: 'name',
      render: (_, repo) => (
        <div>
          <div className="font-medium">{repo.name}</div>
          {repo.fullName && repo.fullName !== repo.name && (
            <div className="text-xs text-gray-500">{repo.fullName}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        if (!status) return null
        const color = HEALTHY_STATUSES.includes(status) ? 'green' : 'default'
        return <Tag color={color}>{status}</Tag>
      },
    },
    {
      title: (
        <span>
          Active for Axolo&nbsp;
          <Tooltip title="When off, Axolo stops creating PR channels and reacting to events for this repository. Existing channels remain unchanged.">
            <InfoCircleTwoTone twoToneColor="#0049ff" />
          </Tooltip>
        </span>
      ),
      dataIndex: 'openingPR',
      key: 'openingPR',
      align: 'right',
      render: (openingPR, repo) => (
        <Switch
          checked={openingPR !== false}
          loading={!!pendingIds[repo.id]}
          disabled={!isRepoActionable(repo)}
          onChange={(value) => handleToggle(repo, value)}
        />
      ),
    },
  ]

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <Input.Search
          placeholder="Search repositories"
          allowClear
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
        />
        {onBulkToggle && (
          <Space>
            <Button onClick={() => onBulkToggle(true)}>Activate all</Button>
            <Button onClick={() => onBulkToggle(false)}>Deactivate all</Button>
          </Space>
        )}
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 25, showSizeChanger: false }}
      />
    </div>
  )
}
